import Link from "next/link";
import { useTranslations } from "next-intl";
import { CookieConsentConfig } from "@/lib/nexus-app";
import { RichTextOrFallback } from "./RichTextOrFallback";
import { CookieConsentNecessaryCard } from "./CookieConsentNecessaryCard";
import { CookieConsentAllCookiesCard } from "./CookieConsentAllCookiesCard";
import {
  BANNER_LINK_CLASS,
  FOOTER_LINK_CLASS,
} from "./utils/cookie-consent.utils";

export function CookieConsentSettingsModal({
  content,
  t,
  onClose,
  onAcceptNecessary,
  onAcceptAll,
}: {
  content: CookieConsentConfig | null | undefined;
  t: ReturnType<typeof useTranslations<"CookieConsent">>;
  onClose: () => void;
  onAcceptNecessary: () => void;
  onAcceptAll: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[100000] overflow-y-auto bg-dark/80 p-4 sm:p-6">
      <div className="flex min-h-full items-center justify-center">
        <div className="w-full max-w-5xl rounded-2xl bg-white shadow-3">
          <div className="border-b border-gray-3 p-5 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-xl font-semibold text-dark sm:text-custom-xl">
                {content?.modalTitle || t("modalTitle")}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-3 px-3 py-1.5 text-sm text-dark transition-colors hover:bg-gray-1"
              >
                {content?.closeLabel || t("close")}
              </button>
            </div>
            <p className="mt-4 text-sm leading-6 text-body">
              <RichTextOrFallback
                value={content?.modalDescription}
                fallback={t.rich("modalDescription", {
                  privacyLink: (chunks) => (
                    <Link href="/privacy-policy" className={BANNER_LINK_CLASS}>
                      {chunks}
                    </Link>
                  ),
                })}
                linkClassName={BANNER_LINK_CLASS}
              />
            </p>
          </div>

          <div className="grid gap-4 p-5 sm:grid-cols-2 sm:gap-6 sm:p-8">
            <CookieConsentNecessaryCard
              content={content}
              t={t}
              onAccept={onAcceptNecessary}
            />
            <CookieConsentAllCookiesCard
              content={content}
              t={t}
              onAccept={onAcceptAll}
            />
          </div>

          <div className="px-5 pb-6 text-sm leading-6 text-body sm:px-8 sm:pb-8">
            <RichTextOrFallback
              value={content?.footerText}
              fallback={t.rich("footerText", {
                privacyLink: (chunks) => (
                  <Link href="/privacy-policy" className={FOOTER_LINK_CLASS}>
                    {chunks}
                  </Link>
                ),
              })}
              linkClassName={FOOTER_LINK_CLASS}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
